import { FeatureFlag } from '@ps/web-core-enums';
import { DebugKey, canUseDOM, createDebug, tryParse } from '@ps/web-core-utils';
import * as Sentry from '@sentry/react';
import type { Client } from '@stomp/stompjs';
import { Frame, over } from '@stomp/stompjs';
import { merge, repeat } from 'lodash';
import type { Observer } from 'rxjs';
import { Observable, from } from 'rxjs';
import { shareReplay, startWith, switchMap } from 'rxjs/operators';
import SockJS from 'sockjs-client';

import { getState, select } from '@swf/core/store';

import type { APIConfig } from './config';
import { ConnectionType, getConfig } from './config';

export type { Frame as Message } from '@stomp/stompjs';

export enum State {
  CLOSED,
  CONNECTING,
  CONNECTED,
  DISCONNECTED
}

export interface Connection {
  config?: APIConfig;
  client?: Client;
  state: State;
  disconnectReason?: string;
}

export { ConnectionType } from './config';

const subjects: { [key: string]: Observable<Connection> } = {};

const socketDebug = createDebug(DebugKey.Socket);
const frameDebug = createDebug(DebugKey.Frame);

const debug = (message: string) => {
  const prefix = message.substring(0, 4);
  const { frames } = Frame.unmarshall(message.substring(4));

  if (!frames.length) {
    socketDebug(message);
    return;
  }

  frames.forEach((frame: Frame) => {
    const body = tryParse(frame.body);
    // eslint-disable-next-line no-control-regex
    const head = prefix + Frame.marshall(frame.command, frame.headers).replace(/\n\n\x00$/, '');

    if (body && canUseDOM) {
      frameDebug(`%s%o`, head, body);
    } else if (body) {
      const split = head.split('\n');
      split.pop();
      frameDebug(`${repeat('%s\n', Object.keys(split).length)}%o`, ...split, body);
    } else {
      frameDebug(head);
    }
  });
};

const connectClient = (config: APIConfig, client: Client, observer: Observer<Connection>) => {
  if (client.connected) {
    return;
  }

  observer.next({ state: State.CONNECTING, client, config });
  let connectionTimeout: NodeJS.Timeout;
  client.connect(
    { ...(config.getConnectionHeaders?.() || {}) },
    () => {
      connectionTimeout = setTimeout(() => (client.reconnect_delay = 0), 5000);
      observer.next({ state: State.CONNECTED, client, config });
    },
    () => null,
    ({ reason: disconnectReason, code, wasClean }) => {
      clearTimeout(connectionTimeout);
      client.reconnect_delay = config.getReconnect(client.reconnect_delay, disconnectReason);
      client.headers = config.getConnectionHeaders?.() || {};
      observer.next({ config, state: State.DISCONNECTED, client, disconnectReason });
      socketDebug('Lost connection from server: %s', disconnectReason);
      if (
        select.feature.check(getState(), FeatureFlag.WPEnableWsDisconnectSentryReporting) &&
        (code > 1000 || !wasClean)
      ) {
        Sentry.captureMessage(`Lost connection from server with code ${code}`, { tags: { code } });
      }
    }
  );
};

const disconnectClient = (config: APIConfig, client: Client, observer: Observer<Connection>) =>
  client.disconnect(() => {
    socketDebug('Disconnected from server');
    observer.next({ config, state: State.CLOSED, client });
  });

const makeConnection = (config: APIConfig) =>
  new Observable((observer: Observer<Connection>) => {
    const client = over(() => new SockJS(config.url, null, config.sockJSOptions));
    client.debug = debug;

    if (canUseDOM) {
      document.addEventListener('starspause', () => disconnectClient(config, client, observer));
      document.addEventListener('starsresume', () => connectClient(config, client, observer));
    }

    connectClient(config, client, observer);

    return () => {
      disconnectClient(config, client, observer);
      delete subjects[config.type];
    };
  });

export const connect = (type = ConnectionType.WMT, overrides: Partial<APIConfig> = {}) => {
  if (!subjects[type]) {
    subjects[type] = from(getConfig(type)).pipe(
      switchMap((apiConfig) => makeConnection(merge({}, apiConfig, overrides))),
      startWith({ state: State.DISCONNECTED }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
  return subjects[type];
};
